<template>
    <v-dialog :value="active" max-width="760px" content-class="round" persistent>
        <v-card>
            <v-card-title>
                <Title class="ma-2" text="Editar video"></Title>
            </v-card-title>

            <v-card-text class="pb-0">
                <p>
                    Pega el enlace del video de YouTube. Es el que se muestra en el
                    panel de informaciones, también a quien todavía no inició sesión.
                </p>

                <v-text-field
                    v-model="url"
                    label="Enlace del video"
                    placeholder="https://www.youtube.com/watch?v=..."
                    rounded
                    filled
                    :error-messages="errores"
                    @input="errores = []"
                ></v-text-field>

                <div v-if="previsualizacion" class="video-responsive mb-2">
                    <iframe
                        :src="previsualizacion"
                        frameborder="0"
                        allowfullscreen
                    ></iframe>
                </div>
                <p v-else-if="url" class="text-center font-italic mb-2">
                    Escribe un enlace de YouTube para ver la previsualización.
                </p>
            </v-card-text>

            <v-card-actions>
                <BottomArrowsStep
                    :withoutIcons="true"
                    nextText="Guardar"
                    backText="Cancelar"
                    :backAction="cerrar"
                    :nextAction="guardar"
                    :nextDisabled="!previsualizacion || guardando"
                />
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import Title from "@/components/forms/Title";
import BottomArrowsStep from "@/components/forms/BottomArrowsStep";
import { tablesService } from "@/services";
import { youtubeEmbedUrl } from "@/helpers/video";

// La clave con la que el backend guarda este enlace. El frontend pide el
// recurso por su clave y no por id, así no depende de ningún número.
const CLAVE = "video_charla_practicas";

// Se usa `tablesService` y no la acción `tables/edit` del store porque esa no
// devuelve el cuerpo de la respuesta ni propaga el error: lo convierte en un
// snackbar genérico. Aquí hacen falta las dos cosas —la URL ya normalizada que
// devuelve el backend, y el mensaje de validación cuando el enlace no es de
// YouTube— para mostrarlas en el campo en vez de un "no se pudo guardar".

export default {
    components: { Title, BottomArrowsStep },

    props: {
        active: {
            type: Boolean,
            default: false,
        },
    },

    data: () => ({
        url: "",
        errores: [],
        guardando: false,
    }),

    computed: {
        // Se calcula en el navegador solo para que la previsualización aparezca
        // al momento. Lo que queda guardado es lo que devuelve el backend, que
        // valida y normaliza por su cuenta.
        previsualizacion() {
            return youtubeEmbedUrl(this.url);
        },
    },

    watch: {
        active(abierto) {
            if (abierto) {
                this.cargar();
            }
        },
    },

    created() {
        if (this.active) {
            this.cargar();
        }
    },

    methods: {
        cargar() {
            this.errores = [];
            tablesService
                .getService(`recursoinfoproceso/${CLAVE}`)
                .then((recurso) => {
                    this.url = recurso.url;
                })
                .catch(() => {
                    this.errores = ["No se pudo cargar el enlace actual."];
                });
        },

        guardar() {
            this.guardando = true;
            tablesService
                .editService("recursoinfoproceso", `${CLAVE}/`, { url: this.url })
                .then((recurso) => {
                    this.guardando = false;
                    // El backend devuelve la URL ya normalizada: se adopta la
                    // suya para que el campo muestre exactamente lo guardado.
                    this.url = recurso.url;
                    this.$store.dispatch("alert/success", "Video actualizado.");
                    this.$emit("guardado", recurso.url);
                    this.$emit("cerrar");
                })
                .catch((error) => {
                    this.guardando = false;
                    this.errores = this.mensajesDeError(error);
                });
        },

        mensajesDeError(error) {
            const datos = error && error.response && error.response.data;
            if (datos && datos.url) {
                return Array.isArray(datos.url) ? datos.url : [datos.url];
            }
            return ["No se pudo guardar el enlace."];
        },

        cerrar() {
            this.errores = [];
            this.$emit("cerrar");
        },
    },
};
</script>

<style scoped>
/* Mismas proporciones que el panel de informaciones, para que lo que se ve
   aquí sea lo que se va a ver allá. */
.video-responsive {
    height: 0;
    overflow: hidden;
    padding-bottom: 56.25%;
    position: relative;
    border-radius: 10px;
    margin: 0 auto;
}
.video-responsive iframe {
    height: 100%;
    left: 0;
    position: absolute;
    top: 0;
    width: 100%;
    border-radius: 10px;
}
</style>
